import {combineLatest} from 'rxjs';
import {filter, map, startWith} from 'rxjs/operators';
import {numUtil, plus} from '@/helper/num';
import {uiMsg$} from '@/ui';
import {currenciesCurrencyCache$} from '@/config';
import type {Assets, AssetsData, AssetsOverview, CurrencyEntity, WalletType} from '@/types';

export interface FUAssetsData {
  type: 'assets';
  data: AssetsData;
}

const btcCurrency$ = currenciesCurrencyCache$.pipe(
  filter((cache) => cache.has('BTC')),
  map((cache) => cache.get('BTC')),
  startWith(null)
);

const assetsData$ = uiMsg$.pipe(
  filter((o) => o.type === 'assets'),
  map((o: FUAssetsData): AssetsData => o.data)
);

export const assets$ = combineLatest([assetsData$, btcCurrency$]).pipe(
  filter(([assetsData, btcCurrency]) => !!assetsData && !!btcCurrency),
  map(([o, btcCurrency]: [AssetsData, CurrencyEntity]): AssetsOverview => convertsAssets(o, btcCurrency))
);

const walletTypeMap: Record<WalletType, string> = {
  SPOT: 'spot',
  SPOT_STRATEGY: 'spotStrategy',
  SPOT_MARGIN: 'spotMargin',
  CONTRACT: 'contract',
  CONTRACT_STRATEGY: 'contractStrategy',
  COPY_TRADE: 'copyTrade',
  FIAT: 'fiat',
  WM: 'investment',
  LAUNCH_POOL: 'investment',
  LENDING: 'lending',
  UTA_TRADE: 'trading',
  UTA_FUND: 'funding',
  MEME: 'memeTrade',
};
const otherSumWalletTypeMap = {
  spotAll: ['spot', 'spotStrategy'],
  contractAll: ['contract', 'copyTrade', 'contractStrategy'],
  tradingAll: ['trading', 'spotStrategy'],
  tradingBot: ['spotStrategy', 'contractStrategy'],
};

function convertsAssets(assets: AssetsData, btcCurrency): AssetsOverview {
  const overview = convertDisplayAssets(
    assets.btcTotalAssets,
    assets.usdTotalAssets,
    assets.usdtTotalAssets,
    assets.localCcyTotalAssets,
    btcCurrency.valuePrecision
  );

  const details = assets.details
    .reduce((p, c) => {
      const key = walletTypeMap[c.walletType];
      let {btcTotalAssets, usdTotalAssets, usdtTotalAssets, localCcyTotalAssets} = c;
      if (p.has(key)) {
        const prev = p.get(key);
        btcTotalAssets = plus(btcTotalAssets, prev.btcEv);
        usdTotalAssets = plus(usdTotalAssets, prev.usdEv);
        usdtTotalAssets = plus(usdtTotalAssets, prev.usdtEv);
        localCcyTotalAssets = plus(localCcyTotalAssets, prev.moneyEv);
      }
      return p.set(key, {
        btcEv: btcTotalAssets,
        usdEv: usdTotalAssets,
        usdtEv: usdtTotalAssets,
        moneyEv: localCcyTotalAssets,
      });
    }, new Map())
    .toObject();

  const details2 = {
    ...details,
    spotAll: calcSumWallets(otherSumWalletTypeMap.spotAll, details),
    contractAll: calcSumWallets(otherSumWalletTypeMap.contractAll, details),
    tradingAll: calcSumWallets(otherSumWalletTypeMap.tradingAll, details),
    tradingBot: calcSumWallets(otherSumWalletTypeMap.tradingBot, details),
  };

  const defaultAsset = convertDisplayAssets(0, 0, 0, 0, btcCurrency.valuePrecision);

  return {
    ...overview,
    localCurrency: assets.localCurrency,
    details: {
      spot: defaultAsset,
      contract: defaultAsset,
      contractStrategy: defaultAsset,
      copyTrade: defaultAsset,
      fiat: defaultAsset,
      lending: defaultAsset,
      spotStrategy: defaultAsset,
      spotMargin: defaultAsset,
      trading: defaultAsset,
      funding: defaultAsset,
      investment: defaultAsset,
      spotAll: defaultAsset,
      contractAll: defaultAsset,
      tradingAll: defaultAsset,
      tradingBot: defaultAsset,
      memeTrade: defaultAsset,
      ...Object.keys(details2).reduce(
        (p, key) => ({
          ...p,
          [key]: convertDisplayAssets(
            details2[key].btcEv,
            details2[key].usdEv,
            details2[key].usdtEv,
            details2[key].moneyEv,
            btcCurrency.valuePrecision
          ),
        }),
        {}
      ),
    },
  };
}

function calcSumWallets(types, details) {
  return types.reduce(
    (p, type) => {
      const c = details[type];
      if (!c) {
        return p;
      }
      return {
        btcEv: plus(p.btcEv, c.btcEv),
        usdEv: plus(p.usdEv, c.usdEv),
        usdtEv: plus(p.usdtEv, c.usdtEv),
        moneyEv: plus(p.moneyEv, c.moneyEv),
      };
    },
    {btcEv: 0, usdEv: 0, usdtEv: 0, moneyEv: 0}
  );
}

function convertDisplayAssets(btcEv, usdEv, usdtEv, moneyEv, btcValuePrecision): Assets {
  const USD_PRECISION = 2;
  const MONEY_PRECISION = 2;
  return {
    btcEv,
    btc: convertDisplayValue(btcEv || 0, btcValuePrecision),
    usdEv,
    usd: convertDisplayValue(usdEv || 0, USD_PRECISION),
    money: convertDisplayValue(moneyEv || 0, MONEY_PRECISION),
    usdtEv,
    usdt: convertDisplayValue(usdtEv || 0, USD_PRECISION),
  };
}

function convertDisplayValue(value, precision = 8) {
  return numUtil.delimit(numUtil.floor(value || 0, precision, 0));
}
